import { KatalComponent, Keys } from '../../shared/base';
import { createElement } from '../../shared/utils';

/*
    Simple key-value store for holding localization data for kat-calendar
    This should be expanded to other components that require localization data, 
    and refactored into its own file/package. Ideally, a localization cache could be used by many components on a page, not just kat-calendars. See https://quip-amazon.com/Vtl8A7HmGkaL/Katal-internationalization-tag-library-proposal
    for a localization proposal that includes caching for maximum performance.
*/
const LOCALIZATION_CACHE = new Map();

const DAYS_IN_A_WEEK = 7;
const MAX_DAYS_TO_DISPLAY = DAYS_IN_A_WEEK * 6;

function localizeDate(date, locale, options) {
  const key = `${locale}_${JSON.stringify(options)}`;
  let val = LOCALIZATION_CACHE.get(key);
  if (!val) {
    val = date.toLocaleString(locale, options);
    LOCALIZATION_CACHE.set(key, val);
  }
  return val;
}

/**
 * @component {kat-calendar} KatalCalendar Calendar component
 * @example USCalendar {"locale": "en-US"}
 * @event change A change event will fire when a date has been selected
 * @status Production
 * @theme flo
 */
class KatalCalendar extends KatalComponent {
  static get observedAttributes() {
    return ['value', 'locale'];
  }

  constructor() {
    super([...KatalCalendar.observedAttributes, 'day-formatter']);

    this.dateRegExp = /^(\d{1,2})([/-])(\d{1,2})([/-])(\d{4})\b/;
    this.current = new Date();
    this.valueDate = '';
    this.previous = {};
    this.today = new Date();
    this.daysLong = [];
    this.daysShort = [];
    this.monthsLong = [];
    this.monthsShort = [];
    this.initial = true;
    this.keyPressFocusSwitch = false;
    this.silent = false;
    this.calendarContainer = this.createCalendarContainer();
    this.dayContainer = this.createDayContainer();
    this.dayOfWeekContainer = this.createDayofWeekNode();

    this._monthChangeKeyHandler = this._monthChangeKeyHandler.bind(this);
  }

  connectedCallback() {
    super.connectedCallback();

    if (this.value) {
      this.current = this.dateCopy(this.value);
    }

    setTimeout(() => {
      this.daysOfWeekbyLocale();
      this.monthsOfYearbyLocale();

      this.initial = false;
      this.render();

      this.catchEvents();
    }, 0);
  }

  catchEvents() {
    let container = this.querySelector('.calendar');
    let events = ['keyup', 'keydown', 'keypressed', 'click'];
    events.forEach(key =>
      container.addEventListener(key, e => e.stopImmediatePropagation())
    );
  }

  catchFocusout() {
    const calendar = this.querySelector('.calendar');
    calendar.addEventListener('focusout', e => {
      if (this.silent) {
        e.stopImmediatePropagation();
      }
    });
  }

  /**
   * @param {string} value This is the text that will be displayed to the user
   * @required
   */
  set value(value) {
    this.setValue(value ? this.toDate(value) : '');
  }

  get value() {
    return this.valueDate;
  }

  /**
   * @param {string} locale This is the locale the calendar should be using
   */
  set locale(value) {
    this.setAttribute('locale', value);
  }

  get locale() {
    return this.getAttribute('locale') || 'en-US';
  }

  // Respond to attribute changes.
  attributeChangedCallback(attr, oldValue, newValue) {
    if (!this.initial) {
      if (oldValue === newValue) {
        return;
      }

      if (attr === 'locale') {
        this.daysOfWeekbyLocale();
        this.monthsOfYearbyLocale();
      }

      if (attr === 'value') {
        this.value = newValue;
        return;
      }

      this.render();
    }
  }

  createDayContainer() {
    return document.createElement('div');
  }

  createDayofWeekNode() {
    let dayOfWeek = document.createElement('div');
    dayOfWeek.classList.add('day-of-week');

    return dayOfWeek;
  }

  createCalendarContainer() {
    const div = document.createElement('div');
    const span = document.createElement('span');
    const icon = document.createElement('kat-icon');
    icon.setAttribute('size', 'small');

    const calendar = div.cloneNode(false);
    calendar.classList.add('calendar');
    const calHeader = div.cloneNode(false);
    calHeader.classList.add('cal-header');
    const prevMonthBtn = createElement(
      'span',
      {
        class: 'cal-lft',
        'aria-label': 'Previous Month',
        tabindex: 0,
      },
      [createElement('kat-icon', { name: 'chevron-left', size: 'small' })]
    );

    const calMonth = span.cloneNode(false);
    calMonth.classList.add('cal-month');
    calMonth.setAttribute('role', 'presentation');
    const nextMonthBtn = createElement(
      'span',
      {
        class: 'cal-rgt',
        'aria-label': 'Next Month',
        tabindex: 0,
      },
      [createElement('kat-icon', { name: 'chevron-right', size: 'small' })]
    );

    const calContainer = div.cloneNode(false);
    calContainer.classList.add('cal-container');

    calHeader.appendChild(prevMonthBtn);
    calHeader.appendChild(calMonth);
    calHeader.appendChild(nextMonthBtn);

    calendar.appendChild(calHeader);
    calendar.appendChild(calContainer);

    return calendar;
  }

  toDate(value) {
    if (typeof value !== 'string') {
      return value;
    }

    let date = new Date(-1);

    // 11/20/2000
    let parts = this.dateRegExp.exec(value);
    if (parts && parts[2] === parts[4]) {
      date = new Date(+parts[5], +parts[1] - 1, +parts[3]);
    }

    return date;
  }

  getFormattedValue() {
    return this.valueDate === this.today
      ? ''
      : this.valueDate
      ? this.format(this.valueDate)
      : '';
  }

  format(date) {
    function pad(num) {
      return (num < 10 ? '0' : '') + num;
    }

    const delimeter = '/',
      year = date.getFullYear(),
      month = date.getMonth() + 1,
      day = date.getDate();

    return [pad(month), pad(day), year].join(delimeter);
  }

  emitEvent(silent) {
    const date = this.valueDate;

    const event = {
      value: this.getFormattedValue(),
      silent,
      date,
    };

    this.dispatchCustomEvent('change', event);
  }

  emitDisplayEvents() {
    const month = this.current.getMonth(),
      year = this.current.getFullYear();

    if (
      !this.noEvents &&
      (month !== this.previous.month || year !== this.previous.year)
    ) {
      // Defer this event so we don't block rendering
      setTimeout(() => {
        this.dispatchCustomEvent('display-change', {
          month: month,
          year: year,
        });
      }, 25);
    }

    this.noEvents = false;
    this.previous = {
      month: month,
      year: year,
    };
  }

  onHide() {
    // not an attribute; called by owner
  }

  onShow() {
    if (this.valueDate) {
      this.current = this.dateCopy(this.valueDate);
    }

    this.render();
  }

  setValue(valueObject) {
    this.valueDate = valueObject;
    if (this.valueDate) {
      this.current = this.dateCopy(this.valueDate);
    }

    this.render();
  }

  onClickDay(node, silent) {
    // Do nothing if disabled
    const isDisabled = node.classList.contains('disabled');
    if (isDisabled) {
      return;
    }

    const day = +node.textContent;
    const isFuture = node.classList.contains('future');
    const isPast = node.classList.contains('past');

    this.current.setDate(day);
    if (isFuture) {
      this.current.setMonth(this.current.getMonth() + 1);
    }
    if (isPast) {
      this.current.setMonth(this.current.getMonth() - 1);
    }

    this.valueDate = this.dateCopy(this.current);

    this.emitEvent(silent);

    if (isFuture || isPast) {
      this.render();
    } else {
      this.selectDay();
    }
  }

  selectDay() {
    let now = this.querySelector('.selected');
    let node = this.dayMap[this.current.getDate()];
    if (now) {
      now.classList.remove('selected');
    }
    node.classList.add('selected');
  }

  focusDay() {
    let container = this.querySelector('.cal-container');
    let node =
      container.querySelector('div.highlighted[tabindex="0"]') ||
      container.querySelector('div.selected[tabindex="0"]');
    if (node) {
      node.focus();
    }
  }

  highlightDay(date) {
    this.keyPressFocusSwitch = true;
    let container = this.querySelector('.cal-container');
    let node;

    node = container.querySelector('div[tabindex="0"]');
    if (node) {
      node.setAttribute('tabindex', '-1');
    }

    const shouldRerender =
      date.getMonth() !== this.current.getMonth() ||
      date.getFullYear() !== this.current.getFullYear();

    this.current = date;
    if (shouldRerender) {
      this.render();
    } else {
      node = container.querySelector(
        `div[aria-label="${this.current.getDate()}"]`
      );
      let days = Array.prototype.slice.call(container.querySelectorAll('.day'));
      days.map(element => {
        element.classList.remove('highlighted');
        element.setAttribute('tabIndex', '-1');
      });

      node.setAttribute('tabindex', '0');
      node.classList.add('highlighted');
      node.focus();
    }
    this.focusDay();
  }

  onClickMonth(direction) {
    let currentDayOfMonth = this.current.getDate();
    this.current.setDate(1);
    this.current.setMonth(this.current.getMonth() + direction);

    this.current.setDate(
      Math.min(currentDayOfMonth, this.numberOfDaysInMonth(this.current))
    );

    this.render();
  }

  createElementFromHTML(htmlString) {
    let div = document.createElement('div');
    div.innerHTML = htmlString.trim();

    // Change this to div.childNodes to support multiple top-level nodes
    return div.firstChild;
  }

  /**
   * Used to define a function that is called each time a day is rendered.
   * @param {function} func Function called each time a day is rendered. Passed two parameters, the day element that is rendered and the date it represents.
   * @katalignore
   */
  get dayFormatter() {
    return this._dayFormatter;
  }
  set dayFormatter(func) {
    this._dayFormatter = func;
    this.render();
  }

  render() {
    this.silent = true;
    // dateNum increments, starting with the first Sunday
    // showing on the monthly calendar. This is usually the
    // previous month, so dateNum will start as a negative number
    this.innerHTML = '';

    this.dayMap = {};

    let calBody = this.createElementFromHTML(`<div class="cal-body"></div>`),
      dayContent,
      day,
      css,
      incDate = this.dateCopy(this.current),
      daysInMonth = this.numberOfDaysInMonth(this.current),
      dateNum = this.getFirstSunday(this.current),
      dateToday = this.getSelectedDate(this.today, this.current),
      dateSelected = this.getSelectedDate(this.valueDate, this.current, true),
      highlighted = this.current.getDate();

    let template = this.calendarContainer.cloneNode(true);

    let monthNode = template.querySelector('.cal-month');
    monthNode.innerText =
      this.monthsLong[this.current.getMonth()] +
      ' ' +
      this.current.getFullYear();

    let container = template.querySelector('.cal-container');

    for (let i = 0; i < DAYS_IN_A_WEEK; i++) {
      let dayofweek = this.dayOfWeekContainer.cloneNode(false);
      dayofweek.innerText = this.daysShort[i];
      calBody.appendChild(dayofweek);
    }

    for (let i = 0; i < MAX_DAYS_TO_DISPLAY; i++) {
      let tabIndex = -1;

      const calendarDay = this.dayContainer.cloneNode(true);

      // current month
      if (dateNum + 1 > 0 && dateNum + 1 <= daysInMonth) {
        dayContent = dateNum + 1;

        // Keep a map of all the days
        // use it for adding and removing selection/hover classes
        incDate.setDate(dayContent);
        calendarDay._date = incDate.getTime();
        this.dayMap[dayContent] = calendarDay;

        css = 'day on';
        if (dateNum + 1 === daysInMonth) {
          css += ' last-day';
        }
        if (dateToday === dayContent) {
          css += ' today';
        }
        if (dateSelected === dayContent) {
          if (this.valueDate) {
            tabIndex = 0;
            css += ' selected';
          }
        } else if (dayContent === highlighted) {
          tabIndex = 0;
          css += ' highlighted';
        }
      } else if (dateNum < 0) {
        // previous month
        dayContent = '';
        css = 'day off past';
      } else {
        // next month
        dayContent = '';
        css = 'day off future';
      }

      calendarDay.setAttribute('class', css);
      calendarDay.setAttribute('tabindex', tabIndex);
      calendarDay.setAttribute('aria-label', dayContent);

      // Run the custom formatter if present
      if (this._dayFormatter && dayContent !== '') {
        this._dayFormatter(calendarDay, new Date(incDate.getTime()));
      }

      let calendarDaySpan = document.createElement('span');
      calendarDaySpan.innerText = dayContent;

      calendarDay.appendChild(calendarDaySpan);
      calBody.appendChild(calendarDay);

      dateNum++;
    }

    container.appendChild(calBody);
    this.appendChild(template);

    this.emitDisplayEvents();

    this.silent = false;
    this.eventHandler();
  }

  getFirstSunday(date) {
    // returns a negative index related to the 1st of the month
    let newDate = new Date(date.getTime());
    newDate.setDate(1);
    return -newDate.getDay();
  }

  getDaysInPrevMonth(date) {
    let newDate = new Date(date);
    newDate.setMonth(newDate.getMonth() - 1);
    return this.numberOfDaysInMonth(newDate);
  }

  isLeapYear(dateOrYear) {
    const year =
      dateOrYear instanceof Date ? dateOrYear.getFullYear() : dateOrYear;
    return !(year % 400) || (!(year % 4) && !!(year % 100));
  }

  eventHandler() {
    this.catchFocusout();

    const container = this.querySelector('.cal-container');
    // Disable the focus on click for disabled dates
    container.addEventListener('mousedown', evt => {
      const node = evt.target.closest('.day');
      if (!node) return;

      const isDisabled = node.classList.contains('disabled');
      if (isDisabled) {
        evt.preventDefault();
      }
    });
    container.addEventListener('click', e => {
      e.stopImmediatePropagation();
      this.dispatchCustomEvent('pre-click', e);
      const node = e.target.closest('.day');
      if (node) {
        this.onClickDay(node);
      }
    });

    container.addEventListener('keydown', e => {
      let days;
      switch (e.keyCode) {
        case Keys.ArrowLeft:
          days = -1;
          break;
        case Keys.ArrowRight:
          days = 1;
          break;
        case Keys.ArrowUp:
          days = -7;
          break;
        case Keys.ArrowDown:
          days = 7;
          break;
        case Keys.PageUp:
          days = this.numberOfDaysInMonth(this.current);
          break;
        case Keys.PageDown:
          days = -this.numberOfDaysInMonth(this.current);
          break;
        case Keys.Home:
          days = this.isLeapYear(this.current) ? 366 : 365;
          break;
        case Keys.End:
          days = this.isLeapYear(this.current) ? -366 : -365;
          break;
        case Keys.Escape:
          this.dispatchCustomEvent('hide', {}, false);
          return false;
        case Keys.Enter:
          this.onClickDay(e.target);
          break;
        case Keys.Space:
          this.onClickDay(e.target, true);
          this.focusDay();
          return false;
      }

      if (days) {
        this.highlightDay(this.add(this.current, days));
        e.preventDefault();
        e.stopImmediatePropagation();
        return false;
      }
    });

    const calLeft = this.querySelector('.cal-lft');
    calLeft.addEventListener('click', evt => {
      evt.stopPropagation();
      this.onClickMonth(-1);

      // Re-focus the button because re-render caused a new button to be created.
      const button = this.querySelector('.cal-lft');
      button.focus();
    });
    calLeft.addEventListener('keydown', this._monthChangeKeyHandler);

    const calRight = this.querySelector('.cal-rgt');
    calRight.addEventListener('click', evt => {
      evt.stopPropagation();
      this.onClickMonth(1);

      // Re-focus the button because re-render caused a new button to be created.
      const button = this.querySelector('.cal-rgt');
      button.focus();
    });
    calRight.addEventListener('keydown', this._monthChangeKeyHandler);
  }

  add(date, amount) {
    amount = -amount;
    let time = date.getTime(),
      length = (function() {
        const sec = 1000,
          min = sec * 60,
          hr = min * 60,
          day = hr * 24,
          week = day * 7;
        return {
          sec: sec,
          min: min,
          hr: hr,
          day: day,
          week: week,
        };
      })();

    return new Date(time - length.day * amount);
  }

  getSelectedDate(date, current) {
    if (date) {
      if (
        date.getMonth() === current.getMonth() &&
        date.getFullYear() === current.getFullYear()
      ) {
        return date.getDate();
      }
    }

    return -999; // index must be out of range, and -1 is the last day of the previous month
  }

  numberOfDaysInMonth(date) {
    let month = date.getMonth();
    let year = date.getYear();
    return 32 - new Date(year, month, 32).getDate();
  }

  daysOfWeekbyLocale() {
    let todayDay = new Date().getDay();
    let startOfWeek = new Date();
    this.daysLong = [];
    this.daysShort = [];
    startOfWeek.setDate(startOfWeek.getDate() - todayDay);

    for (let i = 0; i < 7; i++) {
      let longDay = localizeDate(startOfWeek, this.locale, {
        weekday: 'long',
        index: i,
      });
      let shortDay = localizeDate(startOfWeek, this.locale, {
        weekday: 'short',
        index: i,
      });

      if (shortDay.indexOf('.') !== -1) {
        shortDay = shortDay.replace('.', '');
      }
      this.daysLong.push(longDay);
      this.daysShort.push(shortDay);

      startOfWeek.setDate(startOfWeek.getDate() + 1);
    }
  }

  monthsOfYearbyLocale() {
    let todayMonth = new Date().getMonth();
    let startOfYear = new Date();
    this.monthsLong = [];
    this.monthsShort = [];
    startOfYear.setDate(1);
    startOfYear.setMonth(startOfYear.getMonth() - todayMonth);

    for (let i = 0; i < 12; i++) {
      let longMonth = localizeDate(startOfYear, this.locale, {
        month: 'long',
        index: i,
      });
      let shortMonth = localizeDate(startOfYear, this.locale, {
        month: 'short',
        index: i,
      });

      this.monthsLong.push(longMonth);
      this.monthsShort.push(shortMonth);

      startOfYear.setMonth(startOfYear.getMonth() + 1);
    }
  }

  dateCopy(date) {
    function isValidObject(date) {
      let ms;
      if (typeof date === 'object' && date instanceof Date) {
        ms = date.getTime();
        return !isNaN(ms);
      }
      return false;
    }

    if (isValidObject(date)) {
      return new Date(date.getTime());
    }
    return date;
  }

  _monthChangeKeyHandler(evt) {
    if (evt.keyCode === Keys.Escape) {
      this.dispatchCustomEvent('hide', {}, false);
      return false;
    } else if (evt.keyCode === Keys.Enter || evt.keyCode === Keys.Space) {
      evt.target.click();
    }
  }
}

KatalComponent.register('kat-calendar', KatalCalendar);
